import { createStorefrontApiClient } from '@shopify/storefront-api-client'
import { PRODUCT_QUERY, PRODUCT_BY_HANDLE_QUERY } from '../graphql/product'

// Helper function to find a variant based on selected options
export const variantForOptions = (product, selectedOptions) => {
  if (!product || !product.variants) return null

  return product.variants.find(variant => {
    if (!variant.selectedOptions) return false
    
    return variant.selectedOptions.every(option => {
      return selectedOptions[option.name] === option.value
    })
  })
}

// Helper function to fetch a product by ID
export const fetchProduct = async (productId) => {
  try {
    // Create client instance
    const client = createStorefrontApiClient({
      storeDomain: `${process.env.GATSBY_SHOPIFY_DOMAIN || "minus18"}.myshopify.com`,
      publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
      apiVersion: '2024-10'
    })

    // Convert old SDK ID format to new GraphQL format if needed
    const id = productId.includes('gid://') ? productId : `gid://shopify/Product/${productId.replace('gid://shopify/Product/', '')}`
    
    const { data, errors } = await client.request(PRODUCT_QUERY, {
      variables: { id }
    })
    
    if (errors?.length) {
      console.error('Product fetch errors:', errors)
      throw new Error(errors[0].message)
    }
    
    if (!data || !data.product) {
      throw new Error('Product not found')
    }
    
    // Transform the GraphQL response to match the old SDK format
    const product = data.product
    const transformedProduct = {
      id: product.id,
      shopifyId: product.id,
      title: product.title,
      handle: product.handle,
      description: product.description,
      descriptionHtml: product.descriptionHtml,
      variants: product.variants.edges.map(({ node }) => ({
        id: node.id,
        shopifyId: node.id,
        title: node.title,
        price: parseFloat(node.price.amount),
        compareAtPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null,
        available: node.availableForSale,
        availableForSale: node.availableForSale,
        inventoryQuantity: node.quantityAvailable || 0,
        selectedOptions: node.selectedOptions,
        image: node.image ? {
          originalSrc: node.image.url,
          altText: node.image.altText
        } : null
      })),
      options: product.options.map(option => ({
        id: option.id,
        shopifyId: option.id,
        name: option.name,
        values: option.values
      })),
      images: product.images.edges.map(({ node }) => ({
        originalSrc: node.url,
        altText: node.altText
      })),
      media: product.images.edges.map(({ node }) => ({
        image: {
          originalSrc: node.url,
          altText: node.altText
        }
      }))
    }
    
    return transformedProduct
  } catch (error) {
    console.error('Failed to fetch product:', error)
    throw error
  }
}

// Helper function to fetch a product by handle
export const fetchProductByHandle = async (handle) => {
  try {
    // Create client instance
    const client = createStorefrontApiClient({
      storeDomain: `${process.env.GATSBY_SHOPIFY_DOMAIN || "minus18"}.myshopify.com`,
      publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
      apiVersion: '2024-10'
    })

    const { data, errors } = await client.request(PRODUCT_BY_HANDLE_QUERY, {
      variables: { handle }
    })
    
    if (errors?.length) {
      console.error('Product fetch errors:', errors)
      throw new Error(errors[0].message)
    }
    
    if (!data.productByHandle) {
      throw new Error('Product not found')
    }
    
    // Transform the GraphQL response to match the old SDK format
    const product = data.productByHandle
    const transformedProduct = {
      id: product.id,
      shopifyId: product.id,
      title: product.title,
      handle: product.handle,
      description: product.description,
      descriptionHtml: product.descriptionHtml,
      variants: product.variants.edges.map(({ node }) => ({
        id: node.id,
        shopifyId: node.id,
        title: node.title,
        price: parseFloat(node.price.amount),
        compareAtPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null,
        available: node.availableForSale,
        availableForSale: node.availableForSale,
        inventoryQuantity: node.quantityAvailable || 0,
        selectedOptions: node.selectedOptions,
        image: node.image ? {
          originalSrc: node.image.url,
          altText: node.image.altText
        } : null
      })),
      options: product.options.map(option => ({
        id: option.id,
        shopifyId: option.id,
        name: option.name,
        values: option.values
      })),
      images: product.images.edges.map(({ node }) => ({
        originalSrc: node.url,
        altText: node.altText
      })),
      media: product.images.edges.map(({ node }) => ({
        image: {
          originalSrc: node.url,
          altText: node.altText
        }
      }))
    }
    
    return transformedProduct
  } catch (error) {
    console.error('Failed to fetch product:', error)
    throw error
  }
}

// Create a client object that mimics the old SDK structure
export const productHelpers = {
  variantForOptions,
  fetch: fetchProduct,
  fetchByHandle: fetchProductByHandle
}

// Export individual functions for direct use
export { variantForOptions as default }